import { useEffect, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import { Check, X, Pause, RefreshCw, ExternalLink, AlertTriangle } from "lucide-react";
import { toast } from "sonner";
import api from "../../lib/api";
import { PageHeader, Table, Td, StatusBadge } from "../../components/dashboard/Shared";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../../components/ui/dialog";

export default function MT5Approvals() {
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(false);
  const [modal, setModal] = useState(null); // { account, action }

  const load = useCallback(async () => {
    setBusy(true);
    try {
      const { data } = await api.get("/admin/mt5/pending");
      setData(data);
    } catch { setData({ count: 0, accounts: [] }); }
    finally { setBusy(false); }
  }, []);
  useEffect(() => { load(); const t = setInterval(load, 20000); return () => clearInterval(t); }, [load]);

  const perform = async (notes, override) => {
    if (!modal) return;
    try {
      await api.post(`/admin/mt5/${modal.account.account_id}/${modal.action}`, { notes, override_eligibility: !!override });
      toast.success(`Account ${modal.action}d`);
      setModal(null);
      load();
    } catch (err) { toast.error(err.response?.data?.detail || "Action failed"); }
  };

  return (
    <div>
      <PageHeader title="MT5 Approval Queue" sub="Review, approve or reject pending MT5 connections">
        <button onClick={load} disabled={busy} data-testid="mt5-approvals-refresh" className="btn-outline px-3 py-1.5 text-xs flex items-center gap-2 disabled:opacity-50">
          <RefreshCw size={12} className={busy ? "animate-spin" : ""} /> Refresh
        </button>
      </PageHeader>

      {!data ? <div className="skeleton h-64" /> : data.count === 0 ? (
        <div className="card-dark p-12 text-center" data-testid="no-pending-mt5">
          <p className="text-gray-300">No pending MT5 accounts.</p>
          <p className="text-sm text-gray-500 mt-2">All connection requests have been reviewed.</p>
        </div>
      ) : (
        <Table headers={["User", "Broker", "Type", "Login", "Server", "Declared Equity", "Eligibility", "Submitted", "Submitted IP", "KYC", "Actions"]} testId="mt5-approvals-table">
          {data.accounts.map((a) => (
            <tr key={a.account_id} className="border-b border-white/5" data-testid={`mt5-row-${a.account_id}`}>
              <Td>
                <Link to={`/admin/users/${a.user_id}`} className="text-white hover:text-[#00F0FF] inline-flex items-center gap-1">
                  {a.user?.name || a.user?.email} <ExternalLink size={10} className="opacity-50" />
                </Link>
                <p className="text-xs text-gray-500">{a.user?.email} · {a.user?.country || "—"}</p>
              </Td>
              <Td className="tabular text-white">{a.broker}</Td>
              <Td className="text-xs uppercase">{a.account_type || "real"}</Td>
              <Td className="tabular">{a.login}</Td>
              <Td className="text-xs">{a.server}</Td>
              <Td className={`tabular text-xs ${a.eligible ? "text-gray-300" : "text-[#FF3B30]"}`} data-testid={`declared-equity-${a.account_id}`}>
                {a.declared_equity != null ? `$${Number(a.declared_equity).toLocaleString()}` : "—"}
                <span className="block text-[10px] text-gray-600">declared</span>
              </Td>
              <Td data-testid={`eligibility-${a.account_id}`}>
                {a.eligible ? (
                  <span className="text-xs px-2 py-0.5 rounded-full bg-[#00E676]/10 border border-[#00E676]/30 text-[#00E676] inline-flex items-center gap-1">
                    <Check size={10} /> Eligible
                  </span>
                ) : (
                  <span className="text-xs px-2 py-0.5 rounded-full bg-[#FF3B30]/10 border border-[#FF3B30]/30 text-[#FF3B30] inline-flex items-center gap-1" title={a.eligibility_reason || "Below minimum"}>
                    <AlertTriangle size={10} /> Below ${Number(a.min_equity || 1000).toLocaleString()}
                  </span>
                )}
                {!a.eligible && a.eligibility_reason && (
                  <p className="text-[10px] text-gray-600 mt-1 max-w-[140px]">{a.eligibility_reason}</p>
                )}
              </Td>
              <Td className="text-xs tabular text-gray-500">{a.submitted_at ? String(a.submitted_at).slice(0, 16).replace("T", " ") : "—"}</Td>
              <Td className="text-xs tabular">{a.submitted_ip || "—"}</Td>
              <Td><StatusBadge status={a.user?.kyc_status || "none"} /></Td>
              <Td>
                <div className="flex gap-2">
                  <button data-testid={`approve-mt5-${a.account_id}`} onClick={() => setModal({ account: a, action: "approve" })} className="text-xs px-2.5 py-1 rounded bg-[#00E676]/10 border border-[#00E676]/30 text-[#00E676] hover:bg-[#00E676]/15 flex items-center gap-1"><Check size={11} /> Approve</button>
                  <button data-testid={`reject-mt5-${a.account_id}`} onClick={() => setModal({ account: a, action: "reject" })} className="text-xs px-2.5 py-1 rounded bg-[#FF3B30]/10 border border-[#FF3B30]/30 text-[#FF3B30] hover:bg-[#FF3B30]/15 flex items-center gap-1"><X size={11} /> Reject</button>
                  <button data-testid={`suspend-mt5-${a.account_id}`} onClick={() => setModal({ account: a, action: "suspend" })} className="text-xs px-2.5 py-1 rounded bg-[#FFD600]/10 border border-[#FFD600]/30 text-[#FFD600] hover:bg-[#FFD600]/15 flex items-center gap-1"><Pause size={11} /> Suspend</button>
                </div>
              </Td>
            </tr>
          ))}
        </Table>
      )}

      <Dialog open={!!modal} onOpenChange={(o) => !o && setModal(null)}>
        <DialogContent className="bg-[#121212] border border-white/10 text-gray-200 max-w-md">
          <DialogHeader><DialogTitle className="font-display text-white capitalize">{modal?.action} MT5 Account</DialogTitle></DialogHeader>
          {modal && <MT5ActionForm modal={modal} onCancel={() => setModal(null)} onSubmit={perform} />}
        </DialogContent>
      </Dialog>
    </div>
  );
}

const MT5ActionForm = ({ modal, onCancel, onSubmit }) => {
  const [notes, setNotes] = useState("");
  const [override, setOverride] = useState(false);
  const [busy, setBusy] = useState(false);
  const ineligible = modal.action === "approve" && !modal.account.eligible;
  const submit = async () => { setBusy(true); await onSubmit(notes, override); setBusy(false); };
  const colors = { approve: "bg-[#00E676] text-black", reject: "bg-[#FF3B30] text-white", suspend: "bg-[#FFD600] text-black" };
  return (
    <div className="space-y-4">
      <p className="text-sm text-gray-400">
        <span className="text-white">{modal.account.user?.name || modal.account.user?.email}</span> — {modal.account.broker} · {modal.account.login}
      </p>
      {ineligible && (
        <div className="rounded-lg border border-[#FF3B30]/30 bg-[#FF3B30]/[0.07] p-3" data-testid="mt5-eligibility-warning">
          <p className="text-sm text-[#FF9A94] font-medium flex items-center gap-2"><AlertTriangle size={14} /> Ineligible account</p>
          <p className="text-xs text-gray-400 mt-1">{modal.account.eligibility_reason || `Declared equity is below the $${Number(modal.account.min_equity || 1000).toLocaleString()} minimum.`} Approval is blocked unless you explicitly override.</p>
          <label className="flex items-center gap-2 mt-3 text-xs text-gray-300 cursor-pointer">
            <input type="checkbox" data-testid="mt5-override-eligibility" checked={override} onChange={(e) => setOverride(e.target.checked)} className="accent-[#FF3B30]" />
            Override eligibility and approve anyway (recorded in audit log)
          </label>
        </div>
      )}
      <div>
        <label className="label-xs block mb-1">{modal.action === "approve" ? "Notes (optional)" : "Reason"}</label>
        <textarea
          data-testid="mt5-action-notes"
          className="input-dark min-h-[80px]" maxLength={500}
          placeholder={modal.action === "approve" ? "Ready for live trading" : "Why is this being " + modal.action + "d?"}
          value={notes} onChange={(e) => setNotes(e.target.value)}
        />
      </div>
      <div className="flex gap-3">
        <button onClick={onCancel} data-testid="mt5-action-cancel" className="flex-1 btn-outline py-2.5 text-sm">Cancel</button>
        <button onClick={submit} disabled={busy || (ineligible && !override)} data-testid="mt5-action-confirm" className={`flex-1 py-2.5 text-sm font-medium rounded capitalize disabled:opacity-50 ${colors[modal.action]}`}>
          {busy ? "…" : `Confirm ${modal.action}`}
        </button>
      </div>
    </div>
  );
};
